<script lang="ts">
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { format } from '../../../../services/i18n';
	//Helpers
	import { formatSnakeCaseToTitle } from '../../../../helpers/utilities';
	import { real_id_z } from '../../../../helpers/interface_helpers';
	import { makeNumbersPrimaryAndBold } from '../../../../helpers/utilities';
	//Stores
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Button, Checkbox, Input, Text, toast, Container } from '@Shelly/shelly-design-system';
	import { postReq } from 'src/services/http';

	export let device;

	const id = real_id_z(device.id);

	let settings = readDeviceSettings(id)[`Configuration:0`],
		subSettings = ['auto_on', 'auto_off'], //[20||22,19||21]
		waveTranslation = {
			20: device.channel === 0 ? 0 : undefined,
			22: device.channel === 1 ? 0 : undefined,
			19: device.channel === 0 ? 1 : undefined,
			21: device.channel === 1 ? 1 : undefined,
		}, //separate by channel
		settingsTransformed = {}; //prepare empty settings object

	Object.keys(settings).forEach((s, i) => {
		const settingIndex = s;
		const val = settings[s];
		if (Object.keys(waveTranslation).includes(s) && typeof waveTranslation[s] !== 'undefined') {
			//if setting is in waveTranslation extract it
			settingsTransformed[s] = {
				title: subSettings[waveTranslation[s]],
				value: val,
				index: settingIndex,
			};
		}
	});

	let loading = { auto_on: false, auto_off: false };
	let min = 0, //0 means off
		max = 32535;

	// const method = `${get_z_wave_component_from_category(device)}.SetConfig`;

	const unit = $format('select_units_seconds', { default: 'seconds' });

	const set_auto = async (setting) => {
		loading[setting.title] = true;
		//a=call&cc=Configuration&ep=0&m=set&params=[{"parameter":25,"value":0}]

		const params = {
			id: real_id_z(device.id), //gateway's id
			a: 'call', //inclusion command
			cc: 'Configuration', //command class
			m: 'set', //method
			ep: 0, //endpoint
			params: JSON.stringify([{ parameter: +setting.index, value: +setting.value }]), //parameters parameter - setting index, value - setting value
		};
		const res = await postReq(`/fast/device/zw`, {
			...params,
		});

		if (res.isok) {
			params.m = 'get'; //override method
			params.params = JSON.stringify([+setting.index]); //override params
			const res_get: SettingRequest = await postReq(`/fast/device/zw`, {
				...params,
			});

			if (res_get.isok) {
				settingsTransformed[setting.index].value = res_get.data;
			}
			if (+setting.value < 1) {
				toast.open({
					type: 'success',
					title: $format('auto_turn_successfully_disable', {
						values: { auto_turn_state: $format(setting.title) },
					}),
				});
			} else {
				toast.open({
					type: 'success',
					title: $format('auto_turn_successfully_enabled', {
						values: {
							auto_turn_state: $format(setting.title),
							n: setting.value,
						},
					}),
				});
			}
		} else {
			if (res?.errors?.message) {
				toast.open({ type: 'danger', title: res?.errors?.message });
			} else {
				toast.open({ type: 'danger', title: $format('timer_err_toast') });
			}
		}
		loading[setting.title] = false;
	};

	$: errors = {
		auto_on_error:
			+settingsTransformed?.[20]?.value < min ||
			+settingsTransformed?.[20]?.value > max ||
			+settingsTransformed?.[22]?.value < min ||
			+settingsTransformed?.[22]?.value > max
				? $format('error_out_of_range', {
						default: `Value ${
							settings[`auto_on_delay`]
						}${unit} is out of range. Please provide a value within the limits. (${min} ${unit} — ${max} ${unit})`,
						values: {
							value: settings[`auto_on_delay`],
							max,
							min,
							unit,
						},
					})
				: '',
		auto_off_error:
			+settingsTransformed?.[19]?.value < min ||
			+settingsTransformed?.[19]?.value > max ||
			+settingsTransformed?.[21]?.value < min ||
			+settingsTransformed?.[21]?.value > max
				? $format('error_out_of_range', {
						default: `Value ${
							settings[`auto_off_delay`]
						}${unit} is out of range. Please provide a value within the limits. (${min} ${unit} — ${max} ${unit})`,
						values: {
							value: settings[`auto_off_delay`],
							max,
							min,
							unit,
						},
					})
				: '',
	};

	const handle_toggler = async (setting) => {
		if (setting.value !== 0) {
			setting.cache = setting.value;
			setting.value = 0;
		} else {
			setting.value = setting.cache;
		}
	};
</script>

{#each Object.values(settingsTransformed) as setting}
	<Container
		backgroundColor={$showNewLayout ? 'color-0' : undefined}
		type={!$showNewLayout ? 'modalContainer' : undefined}
		padding="xs"
		class="mb-s border-color-transparent"
	>
		<Text element="h2" styleType="h2" class="mb-s">
			{formatSnakeCaseToTitle($format(setting.title))}
		</Text>

		<Checkbox
			size="m"
			checked={settingsTransformed[setting.index].value !== 0}
			class="mb-s"
			on:click={() => handle_toggler(setting)}
			id="checkbox-t3q82gx"
		>
			{@html makeNumbersPrimaryAndBold(
				`${$format(`${setting.title}_description`, {
					values: {
						[`${setting.title}_delay`]: setting.value,
					},
				})}`,
			)}
		</Checkbox>

		<Input
			aria-label={$format(`input_${setting.title}`, {
				default: `Enter the ${setting.title} delay. Value must be between {min} — {max}!`,
				values: { min, max },
			})}
			class="mb-s"
			size="m"
			icon="fa-solid fa-stopwatch"
			bind:value={settingsTransformed[setting.index].value}
			type={'number'}
			error={errors[`${setting.title}_error`]}
			{min}
			{max}
			id="input-bbo0vhv"
		/>

		<Button
			loading={loading[setting.title]}
			disabled={loading[setting.title] || errors[`${setting.title}_error`]}
			size="m"
			width="full"
			on:click={() => set_auto(setting)}>{$format('button_save')}</Button
		>
	</Container>
{/each}
